import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { XCircle, Loader2, Package, Wrench, Boxes, Search, Gift, Plus, Type, RefreshCw, ShieldCheck, AlertTriangle, Settings } from "lucide-react";
import { toast } from "sonner";
import api, { formatPriceExact as formatPrice } from "../../../lib/api";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../ui/dialog";
import InfoHint from "../InfoHint";
import SettingsShortcut from "../../dealer/SettingsShortcut";
import { useAutosaveOnBlur, AutosaveBadge } from "./useAutosaveOnBlur";
import { DispoBackButton } from "./DispoBackButton";
import { FeatureGuideTriangle } from "./FeatureGuideTriangle";
import { useGearsVisible } from "../../../lib/gearVisibility";

// WMS-Kategorie → Anzeige-Label (Jun-2026 User-Wunsch: vereinheitlichte Suche
// über Zubehör, Lohn/AW, Reifen, Ersatzteile in einem Feld).
const CAT_LABELS = { accessory: "Zubehör", labour: "Lohn (AW)", tire: "Reifen", part: "Ersatzteil", fluid: "Betriebsstoff", package: "Paket", sales: "Dienstleistung" };
const CatBadge = ({ cat }) => cat && CAT_LABELS[cat]
    ? <span className="text-[9px] font-bold uppercase tracking-wider bg-gray-100 text-gray-500 rounded-sm px-1 py-0.5 ml-1">{CAT_LABELS[cat]}</span>
    : null;

// Jul-2026 (User-Wunsch) — sich gegenseitig ausschließende Dienstleistungs-Gruppen:
// „Zulassung" (Selbst / Händler / Versicherungskennzeichen) und „Transport"
// (Händlernetz / Bundesgebiet / Kunde-selbst). Je Gruppe ist nur EINE Position
// erlaubt. Erkennung robust über SKU-Präfix + Namensmuster (Artikel sind
// händlerspezifisch, daher keine harten IDs).
const _sku = (r) => String(r?.sku || "").toUpperCase();
const _nm = (r) => String(r?.name || "").toLowerCase();
export const isVersKennzeichen = (r) => _sku(r).includes("VERSICHERUNGSKENNZEICHEN") || (_nm(r).includes("versicherungskennzeichen") && (_sku(r).startsWith("ZULASSUNG") || _nm(r).includes("zulassung")));
export const serviceExclusiveGroup = (r) => {
    if (_sku(r).startsWith("ZULASSUNG") || _nm(r).includes("selbstzulassung") || _nm(r).includes("zulassung durch")) return "zulassung";
    if (_sku(r).startsWith("TRANSPORT") || _nm(r).includes("händlernetz") || _nm(r).includes("haustürlieferung") || _nm(r).includes("bundesgebiet")) return "transport";
    return null;
};
export const hasTransportService = (rows) => (rows || []).some((r) => serviceExclusiveGroup(r) === "transport");
// Jul-2026 (User-Wunsch) — TÜV-Erneuerung: kostenlos bei Restlaufzeit ≤ 6 Monate,
// sonst kostenpflichtig.
export const isTuevErneuern = (r) => {
    const s = _sku(r); const n = _nm(r);
    return s.includes("TÜV-ERNEUERN") || s.includes("TUEV-ERNEUERN") || (n.includes("tüv") && (n.includes("erneuern") || n.includes("neuen tüv")));
};
// Jul-2026 (User-Wunsch) — Bereitstellungskosten: immer Pflicht, nicht entfernbar.
export const isBereitstellung = (r) => _sku(r) === "BEREITSTELLUNG" || _nm(r).includes("bereitstellung");

// Durchsuchbares Auswahlfeld (Autocomplete) zum Hinzufügen von Artikeln/Boni.
// Jun-2026 (User-Wunsch) — volle Breite + klar erkennbare Suche mit Vorschlägen.
function CatalogSearchAdd({ catalog, onAdd, testid, placeholder, emptyText, accent = "orange", labelKey = "name", searchFn = null, onFree = null, topSuggestions = [] }) {
    const [query, setQuery] = useState("");
    const [open, setOpen] = useState(false);
    const [remote, setRemote] = useState([]);
    const [remoteLoading, setRemoteLoading] = useState(false);
    const wrapRef = useRef(null);
    const A = accent === "emerald"
        ? { border: "border-emerald-400", hover: "hover:bg-emerald-50" }
        : { border: "border-orange-400", hover: "hover:bg-orange-50" };
    useEffect(() => {
        const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);
    // Jul-2026 (User-Wunsch) — serverseitige, entprellte Suche über ALLE WMS-Artikel
    // (vollständig + schnell, kein Vorab-Laden einzelner Kategorien mehr nötig).
    useEffect(() => {
        if (!searchFn) return undefined;
        const term = query.trim();
        if (term.length < 2) { setRemote([]); setRemoteLoading(false); return undefined; }
        setRemoteLoading(true);
        const t = setTimeout(async () => {
            try { const res = await searchFn(term); setRemote(Array.isArray(res) ? res : []); }
            catch { setRemote([]); }
            finally { setRemoteLoading(false); }
        }, 250);
        return () => clearTimeout(t);
    }, [query, searchFn]);
    const q = query.trim().toLowerCase();
    // Jul-2026 (User-Wunsch): lokaler Katalog (inkl. Lohn/AW, Boni) ist immer
    // durchblätter- und durchsuchbar — auch Kategorie-Namen („Lohn", „Reifen")
    // treffen. Beim Klick ins Feld klappen die zutreffenden Optionen sofort auf.
    const localMatches = (catalog || []).filter((c) =>
        !q || `${c[labelKey] || ""} ${c.sku || ""} ${CAT_LABELS[c.category] || ""}`.toLowerCase().includes(q)).slice(0, 50);
    let filtered;
    if (searchFn) {
        if (q.length < 2) {
            // Jul-2026 (User-Wunsch): ohne Eingabe die am häufigsten benutzten
            // Artikel ZUERST anzeigen (dann der restliche Katalog).
            const topIds = new Set((topSuggestions || []).map((t) => t.id));
            filtered = [...(topSuggestions || []), ...localMatches.filter((c) => !topIds.has(c.id))].slice(0, 50);
        } else {
            const seen = new Set();
            filtered = [...(remote || []), ...localMatches].filter((c) => {
                if (!c.id || seen.has(c.id)) return false;
                seen.add(c.id);
                return true;
            }).slice(0, 50);
        }
    } else {
        filtered = localMatches;
    }
    const pick = (a) => { onAdd(a); setQuery(""); setOpen(false); setRemote([]); };
    const disabled = searchFn ? false : (!(catalog && catalog.length) && !onFree);
    return (
        <div className="relative w-full" ref={wrapRef} data-testid={`${testid}-search`}>
            <div className={`flex items-center gap-2 border-2 rounded-sm px-3 py-2 bg-white ${open ? A.border : "border-gray-300"}`}>
                <Search className="w-4 h-4 text-gray-400 shrink-0" />
                <input
                    value={query}
                    onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
                    onFocus={() => setOpen(true)}
                    disabled={disabled}
                    placeholder={disabled ? emptyText : placeholder}
                    className="flex-1 text-sm outline-none bg-transparent disabled:bg-transparent disabled:text-gray-400 min-w-0"
                    data-testid={`${testid}-search-input`}
                />
            </div>
            {open && !disabled && (filtered.length > 0 || onFree) && (
                <div className="absolute z-30 left-0 right-0 mt-1 max-h-60 overflow-auto bg-white border border-gray-200 rounded-sm shadow-lg" data-testid={`${testid}-search-options`}>
                    {filtered.map((c) => (
                        <button
                            key={c.id}
                            type="button"
                            onClick={() => pick(c)}
                            className={`dispo-suggest-item block w-full text-left px-3 py-2 text-sm ${A.hover}`}
                            data-testid={`${testid}-search-option-${c.id}`}
                        >
                            <span className="font-medium">{c[labelKey]}</span>
                            {c.sku ? <span className="text-gray-400"> · {c.sku}</span> : null}
                            {c.sell_price_gross != null && <span className="text-gray-500"> · {formatPrice(c.sell_price_gross || 0)}</span>}
                            {c.default_amount != null && <span className="text-emerald-600"> · −{c.default_amount_type === "percent" ? `${c.default_amount} %` : formatPrice(c.default_amount || 0)}</span>}
                            {c._top && <span className="text-[9px] font-bold uppercase tracking-wider bg-orange-100 text-orange-700 rounded-sm px-1 py-0.5 ml-1" title={`${c.usage_count || 0}× in Verkäufen benutzt`}>Häufig{c.usage_count ? ` · ${c.usage_count}×` : ""}</span>}
                            <CatBadge cat={c.category} />
                        </button>
                    ))}
                    {onFree && query.trim().length >= 2 && (
                        <button type="button"
                            onClick={() => { onFree(query.trim()); setQuery(""); setOpen(false); }}
                            className={`block w-full text-left px-3 py-2 text-sm border-t border-dashed border-gray-200 ${A.hover}`}
                            data-testid={`${testid}-search-add-free`}>
                            <Plus className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />
                            <span className="font-medium">„{query.trim()}“</span> als eigene Aktion hinzufügen
                        </button>
                    )}
                </div>
            )}
            {open && !disabled && filtered.length === 0 && !onFree && (
                <div className="absolute z-30 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-sm shadow-lg px-3 py-2 text-xs text-gray-400" data-testid={`${testid}-search-noresults`}>
                    {searchFn && remoteLoading ? "Suche läuft …"
                        : searchFn && query.trim().length < 2 ? "Mind. 2 Zeichen eingeben …"
                        : "Keine Treffer."}
                </div>
            )}
        </div>
    );
}

// ---------- Zubehör & Dienstleistungen (WMS) -------------------------
// Jun-2026 (User-Wunsch) — Zubehör mit hinterlegten Montage-Arbeitswerten (AW)
// erzeugt automatisch eine verknüpfte Montage-Lohnposition (AW × dynamischer
// AW-Satz). `montageRate` = Lohn/AW-Artikel (sell_price_gross = €/AW), `awMap`
// = { accessoryArticleId: assembly_aw }.
function DispoItemsRows({ title, hint, hintId, catalog, rows, setRows, testid, montageRate, awMap, searchFn = null, groupOf = null, onAfterAdd = null, headerExtra = null, montageFlat = null, topSuggestions = [] }) {
    const montageEnabled = Boolean(montageRate && awMap);
    // Jul-2026 (User-Wunsch): jeder Artikel muss montiert werden — ohne
    // hinterlegte Montage-AW automatisch eine Pauschale je Artikel (Zahnrad).
    const flatEligible = (art) => ["accessory", "tire", "part"].includes(art?.category || "accessory");
    const buildFlatMontageRow = (art, qty) => ({
        article_id: montageRate?.id || "montage-flat",
        sku: montageRate?.sku || "",
        name: `Montage: ${art.name}`,
        category: "labour",
        montage_for: art.id,
        montage_flat: true,
        qty: Number(qty) || 1,
        unit_price_gross: Number(montageFlat || 0),
    });
    const buildMontageRow = (art, qty) => {
        const aw = Number((awMap || {})[art.id] || art.assembly_aw || 0);
        return {
            article_id: montageRate.id,
            sku: montageRate.sku,
            name: `Montage: ${art.name}`,
            category: "labour",
            montage_for: art.id,
            aw_per_unit: aw,
            qty: Number((aw * (Number(qty) || 0)).toFixed(2)),
            unit_price_gross: Number(montageRate.sell_price_gross || 0),
        };
    };
    const add = (art) => {
        const grp = groupOf ? groupOf(art) : null;
        setRows((prev) => {
            // Gruppen-Exklusivität: bestehende Positionen derselben Gruppe entfernen.
            let base = grp ? prev.filter((r) => (groupOf ? groupOf(r) : null) !== grp) : prev;
            if (base.some((r) => r.article_id === art.id && !r.montage_for)) return base;
            const next = [...base, { article_id: art.id, sku: art.sku, name: art.name, category: art.category, qty: 1, unit_price_gross: Number(art.sell_price_gross || art.list_price_gross || 0) }];
            if (montageEnabled && Number((awMap || {})[art.id] || art.assembly_aw || 0) > 0) next.push(buildMontageRow(art, 1));
            else if (Number(montageFlat) > 0 && flatEligible(art)) next.push(buildFlatMontageRow(art, 1));
            return next;
        });
        if (onAfterAdd) onAfterAdd(art);
    };
    const upd = (idx, k, v) => setRows((prev) => {
        const target = prev[idx];
        let next = prev.map((r, i) => i === idx ? { ...r, [k]: v } : r);
        // Montage-Menge automatisch nachziehen, wenn sich die Zubehör-Menge ändert.
        if (k === "qty" && target && !target.montage_for) {
            const newQty = Number(v) || 0;
            next = next.map((r) => {
                if (!(r.montage_for && r.montage_for === target.article_id)) return r;
                return r.montage_flat
                    ? { ...r, qty: newQty }
                    : { ...r, qty: Number(((Number(r.aw_per_unit) || 0) * newQty).toFixed(2)) };
            });
        }
        return next;
    });
    const rm = (idx) => setRows((prev) => {
        const target = prev[idx];
        if (target?.locked) return prev; // Pflicht-Position (z. B. Bereitstellung) — nicht entfernbar.
        // Beim Entfernen eines Zubehörs auch dessen verknüpfte Montage-Zeile entfernen.
        if (target && !target.montage_for) {
            return prev.filter((r, i) => i !== idx && !(r.montage_for && r.montage_for === target.article_id));
        }
        return prev.filter((_, i) => i !== idx);
    });
    return (
        <div data-testid={testid}>
            <Label className="text-xs font-bold uppercase tracking-wider flex items-center gap-1.5">{title}<InfoHint id={hintId} defaultText={hint} side="right" />{headerExtra}</Label>
            <div className="mb-1.5" />
            <CatalogSearchAdd catalog={catalog} onAdd={add} testid={testid} searchFn={searchFn} topSuggestions={topSuggestions}
                placeholder="+ Artikel suchen & hinzufügen…" emptyText="Keine WMS-Artikel" />
            {rows.length === 0 && <div className="text-xs text-gray-400 py-1.5">Noch nichts ausgewählt.</div>}
            {rows.map((r, idx) => {
                const isMontage = Boolean(r.montage_for);
                return (
                <div key={`${r.article_id}-${r.montage_for || ""}-${idx}`} className={`flex items-center gap-2 py-1.5 border-b border-gray-100 ${isMontage ? "pl-5 bg-amber-50/40" : ""}`} data-testid={isMontage ? `${testid}-montage-row` : `${testid}-row`}>
                    <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate flex items-center">
                            {r.name}
                            {isMontage
                                ? <span className="text-[9px] font-bold uppercase tracking-wider bg-amber-100 text-amber-700 rounded-sm px-1 py-0.5 ml-1">{r.montage_flat ? "Montage · Pauschale" : `Montage · ${r.aw_per_unit} AW/Stk`}</span>
                                : <CatBadge cat={r.category} />}
                        </div>
                        {r.sku && <div className="text-[10px] text-gray-400">{r.sku}{isMontage ? " · automatisch berechnet" : ""}</div>}
                    </div>
                    <Input type="number" min="0" step={isMontage && !r.montage_flat ? "0.1" : "1"} value={r.qty} onChange={(e) => upd(idx, "qty", e.target.value)} className="w-14 h-8 text-xs" data-testid={`${testid}-qty`} title={isMontage && !r.montage_flat ? "Arbeitswerte (AW)" : "Menge"} />
                    <span className="text-[10px] text-gray-400">×</span>
                    <Input type="number" min="0" step="0.01" value={r.unit_price_gross} onChange={(e) => upd(idx, "unit_price_gross", e.target.value)} className="w-24 h-8 text-xs" data-testid={`${testid}-price`} title={isMontage ? (r.montage_flat ? "Montage-Pauschale € pro Stück" : "€ pro AW") : "Einzelpreis brutto"} />
                    <span className="text-sm font-bold w-24 text-right">{formatPrice((Number(r.qty) || 0) * (Number(r.unit_price_gross) || 0))}</span>
                    {r.locked ? (
                        <span className="text-[9px] font-bold uppercase tracking-wider bg-gray-200 text-gray-600 rounded-sm px-1.5 py-1" title="Pflicht-Position — nicht entfernbar" data-testid={`${testid}-locked`}>Pflicht</span>
                    ) : (
                        <button type="button" onClick={() => rm(idx)} className="text-red-500 hover:text-red-700" data-testid={`${testid}-remove`}><XCircle className="w-4 h-4" /></button>
                    )}
                </div>
                );
            })}
        </div>
    );
}

// ---------- Vorausgewählte Dienstleistungen (Zahnrad, Jul-2026) -------
// Konfiguration: Positionen, die beim Kaufprozess IMMER automatisch aufgenommen
// werden. `locked` = Pflicht (nicht entfernbar, wie Bereitstellung).
function ServiceDefaultsDialog({ open, onClose, searchFn, onSaved }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        if (!open) return;
        setLoading(true);
        api.get("/dealer/dispo/default-service-items")
            .then((r) => setItems(r.data?.items || []))
            .catch(() => setItems([]))
            .finally(() => setLoading(false));
    }, [open]);
    const add = (art) => setItems((prev) => prev.some((i) => i.article_id === art.id) ? prev
        : [...prev, { article_id: art.id, sku: art.sku, name: art.name, category: art.category || "sales", qty: 1, unit_price_gross: Number(art.sell_price_gross || art.list_price_gross || 0), locked: false, ccm_scope: "alle" }]);
    const upd = (idx, k, v) => setItems((prev) => prev.map((i, n) => n === idx ? { ...i, [k]: v } : i));
    const save = async () => {
        setSaving(true);
        try {
            await api.put("/dealer/dispo/default-service-items", { items });
            toast.success("Vorauswahl gespeichert — gilt ab jetzt für jeden Kaufprozess");
            onSaved?.(items);
            onClose();
        } catch (e) { toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen"); }
        finally { setSaving(false); }
    };
    if (!open) return null;
    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-3xl w-[95vw] max-h-none" data-testid="service-defaults-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><Settings className="w-5 h-5 text-gray-600" /> Vorausgewählte Dienstleistungen</DialogTitle>
                </DialogHeader>
                <p className="text-xs text-gray-500 -mt-2">
                    Diese Positionen werden bei jedem Kaufprozess automatisch unter „Dienstleistungen" aufgenommen.
                    <strong> Pflicht</strong> = kann im Kaufprozess nicht entfernt werden, sonst entfernbar.
                    Die <strong>ccm-Auswahl</strong> bindet die Position an die Hubraum-Angabe des Fahrzeugs (bis / über 50 ccm),
                    die <strong>Typ-Auswahl</strong> an den Fahrzeugtyp (Motorrad / Roller / Fahrrad / E-Bike).
                </p>
                <CatalogSearchAdd catalog={[]} onAdd={add} testid="service-defaults" searchFn={searchFn}
                    placeholder="+ Artikel suchen & zur Vorauswahl hinzufügen…" emptyText="Keine WMS-Artikel" />
                {loading ? (
                    <div className="text-xs text-gray-400 py-2 flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Lade Vorauswahl…</div>
                ) : items.length === 0 ? (
                    <div className="text-xs text-gray-400 py-2">Noch keine Vorauswahl definiert.</div>
                ) : items.map((it, idx) => (
                    <div key={it.article_id} className="flex flex-wrap items-center gap-2 py-1.5 border-b border-gray-100" data-testid="service-defaults-row">
                        <div className="flex-1 min-w-[160px]">
                            <div className="text-sm font-medium truncate">{it.name}<CatBadge cat={it.category} /></div>
                            {it.sku && <div className="text-[10px] text-gray-400">{it.sku}</div>}
                        </div>
                        <Input type="number" min="0" step="1" value={it.qty} onChange={(e) => upd(idx, "qty", e.target.value)} className="w-14 h-8 text-xs" title="Menge" data-testid="service-defaults-qty" />
                        <span className="text-[10px] text-gray-400">×</span>
                        <Input type="number" min="0" step="0.01" value={it.unit_price_gross} onChange={(e) => upd(idx, "unit_price_gross", e.target.value)} className="w-24 h-8 text-xs" title="Einzelpreis brutto" data-testid="service-defaults-price" />
                        <select value={it.ccm_scope || "alle"} onChange={(e) => upd(idx, "ccm_scope", e.target.value)}
                            className="h-8 text-[11px] border border-gray-300 rounded-sm px-1 bg-white"
                            title="Hubraum-Bindung: gilt nur für Fahrzeuge mit passender ccm-Angabe"
                            data-testid="service-defaults-ccm">
                            <option value="alle">Alle ccm</option>
                            <option value="bis_50">bis 50 ccm</option>
                            <option value="ueber_50">über 50 ccm</option>
                        </select>
                        <select value={it.vehicle_scope || "alle"} onChange={(e) => upd(idx, "vehicle_scope", e.target.value)}
                            className="h-8 text-[11px] border border-gray-300 rounded-sm px-1 bg-white"
                            title="Fahrzeugtyp-Bindung: gilt nur für Fahrzeuge des gewählten Typs"
                            data-testid="service-defaults-vehicle">
                            <option value="alle">Alle Typen</option>
                            <option value="motorrad">Motorrad</option>
                            <option value="roller">Roller</option>
                            <option value="fahrrad">Fahrrad</option>
                            <option value="ebike">E-Bike</option>
                        </select>
                        <label className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-gray-600 cursor-pointer select-none" title="Pflicht-Position — kann im Kaufprozess nicht entfernt werden">
                            <input type="checkbox" checked={Boolean(it.locked)} onChange={(e) => upd(idx, "locked", e.target.checked)} data-testid="service-defaults-locked" /> Pflicht
                        </label>
                        <button type="button" onClick={() => setItems((prev) => prev.filter((_, n) => n !== idx))} className="text-red-500 hover:text-red-700" data-testid="service-defaults-remove"><XCircle className="w-4 h-4" /></button>
                    </div>
                ))}
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Abbrechen</Button>
                    <Button onClick={save} disabled={saving} className="bg-[#111827] text-white hover:bg-black" data-testid="service-defaults-save">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Vorauswahl speichern"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ---------- Hersteller-Zubehör-Links (Zahnrad, Jul-2026) ---------------
// Pro Marke ein Link zur Hersteller-Zubehörseite; im Kaufprozess wird der zur
// Fahrzeug-Marke passende Link neben der Zubehör-Überschrift angezeigt.
function AccessoryLinksDialog({ open, onClose, onSaved }) {
    const [links, setLinks] = useState([]);
    const [montageFlat, setMontageFlat] = useState("40");
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        if (!open) return;
        setLoading(true);
        api.get("/dealer/dispo/accessory-brand-links")
            .then((r) => {
                setLinks(r.data?.links || []);
                setMontageFlat(String(r.data?.montage_flat_gross ?? 40));
            })
            .catch(() => setLinks([]))
            .finally(() => setLoading(false));
    }, [open]);
    const upd = (idx, k, v) => setLinks((prev) => prev.map((l, n) => n === idx ? { ...l, [k]: v } : l));
    const save = async () => {
        setSaving(true);
        try {
            await api.put("/dealer/dispo/accessory-brand-links", {
                links: links.filter((l) => (l.brand || "").trim() && (l.url || "").trim()),
                montage_flat_gross: Number(String(montageFlat).replace(",", ".")) || 0,
            });
            toast.success("Einstellungen gespeichert — gelten für alle Standorte");
            onSaved?.();
            onClose();
        } catch (e) { toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen"); }
        finally { setSaving(false); }
    };
    if (!open) return null;
    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-3xl w-[95vw] max-h-none" data-testid="accessory-links-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><Settings className="w-5 h-5 text-gray-600" /> Hersteller-Zubehörseiten</DialogTitle>
                </DialogHeader>
                <p className="text-xs text-gray-500 -mt-2">
                    Pro Marke ein Link zur Zubehörseite des Herstellers. Passt die Marke zum Fahrzeug,
                    erscheint der Link gut sichtbar neben „Zubehör, Lohn (AW), Reifen &amp; Ersatzteile".
                    <strong> Anzeigename</strong> (optional) = gekürzte Beschriftung, sonst wird die Domain angezeigt.
                    <span className="block mt-1 font-bold text-gray-600">Diese Einstellungen gelten automatisch für alle Standorte des Händlers.</span>
                </p>
                {loading ? (
                    <div className="text-xs text-gray-400 py-2 flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Lade Links…</div>
                ) : (
                    <>
                        {links.length === 0 && <div className="text-xs text-gray-400 py-1">Noch keine Links hinterlegt.</div>}
                        {links.map((l, idx) => (
                            <div key={idx} className="flex flex-wrap items-center gap-2 py-1" data-testid="accessory-links-row">
                                <Input value={l.brand || ""} onChange={(e) => upd(idx, "brand", e.target.value)} placeholder="Marke (z. B. Vespa)" className="w-36 h-8 text-xs" data-testid="accessory-links-brand" />
                                <Input value={l.url || ""} onChange={(e) => upd(idx, "url", e.target.value)} placeholder="https://…" className="flex-1 min-w-[180px] h-8 text-xs" data-testid="accessory-links-url" />
                                <Input value={l.label || ""} onChange={(e) => upd(idx, "label", e.target.value)} placeholder="Anzeigename (optional)" className="w-40 h-8 text-xs" data-testid="accessory-links-label" />
                                <button type="button" onClick={() => setLinks((prev) => prev.filter((_, n) => n !== idx))} className="text-red-500 hover:text-red-700" data-testid="accessory-links-remove"><XCircle className="w-4 h-4" /></button>
                            </div>
                        ))}
                        <Button type="button" variant="outline" size="sm" className="h-8 text-xs w-fit"
                            onClick={() => setLinks((prev) => [...prev, { brand: "", url: "", label: "" }])}
                            data-testid="accessory-links-add">
                            <Plus className="w-3.5 h-3.5 mr-1" /> Link hinzufügen
                        </Button>
                        <div className="border-t border-gray-200 pt-3 mt-2">
                            <Label className="text-xs font-bold uppercase tracking-wider flex items-center gap-1.5">
                                <Wrench className="w-3.5 h-3.5 text-amber-600" /> Montage-Pauschale pro Artikel (brutto)
                            </Label>
                            <p className="text-[11px] text-gray-500 mt-0.5">
                                Jeder ausgewählte Artikel muss montiert werden — ohne hinterlegte Montage-AW wird
                                automatisch diese Pauschale je Artikel als Montage-Position aufgenommen (0 = keine Automatik).
                            </p>
                            <div className="flex items-center gap-2 mt-1.5">
                                <Input type="number" min="0" step="0.5" value={montageFlat}
                                    onChange={(e) => setMontageFlat(e.target.value)}
                                    className="w-28 h-8 text-sm" data-testid="accessory-montage-flat-input" />
                                <span className="text-xs text-gray-500">€ inkl. USt je Artikel</span>
                            </div>
                            <p className="text-[10px] text-gray-500 mt-1 font-bold" data-testid="montage-flat-org-hint">Gilt immer für den Hauptsitz und alle Standorte des Händlers.</p>
                        </div>
                    </>
                )}
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Abbrechen</Button>
                    <Button onClick={save} disabled={saving} className="bg-[#111827] text-white hover:bg-black" data-testid="accessory-links-save">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Speichern"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ---------- Boni & Aktionen (Abzugsposten, negativ) -------------------
function BoniRows({ catalog, rows, setRows, testid, onAdded }) {
    const add = (b) => {
        if (rows.some((r) => r.bonus_type_id === b.id)) return;
        const isExt = b.visibility === "extern";
        const isAdv = !!b.advantage_only;
        if (isExt) {
            const existingExt = rows.filter((r) => (r.visibility || "intern") === "extern");
            // Jul-2026 (User-Wunsch) — Ausnahme: als „kombinierbar" markierte Aktionen
            // dürfen gemeinsam gewählt werden (Prüfung in beide Richtungen).
            const combinable = existingExt.every((r) => {
                const other = (catalog || []).find((c) => c.id === r.bonus_type_id);
                return (b.combinable_with || []).includes(r.bonus_type_id)
                    || (other?.combinable_with || []).includes(b.id);
            });
            if (existingExt.length && !combinable) {
                toast.error("Es ist nur eine externe Aktion pro Verkauf möglich (Ausnahme: im Boni-Katalog als kombinierbar markierte Aktionen).");
                return;
            }
        }
        const baseAmt = b.default_amount_type === "percent" ? 0 : Number(b.default_amount || 0);
        const amount = (isExt && !isAdv)
            ? (baseAmt || Number(b.external_price || 0))
            : baseAmt;
        setRows((prev) => [...prev, {
            bonus_type_id: b.id, name: b.name, amount,
            visibility: b.visibility || "intern",
            external_price: Number(b.external_price || 0),
            advantage_only: isAdv,
        }]);
        // Jul-2026 (User-Wunsch) — kostenpflichtige Positionen der Aktion automatisch
        // unter Zubehör/Lohn einsetzen (Kunde zahlt).
        if (onAdded) onAdded(b);
    };
    const upd = (idx, k, v) => setRows((prev) => prev.map((r, i) => i === idx ? { ...r, [k]: v } : r));
    const rm = (idx) => setRows((prev) => prev.filter((_, i) => i !== idx));
    // Jul-2026 (User-Wunsch) — EIN Feld: Katalog-Suche + freie Aktion in einem.
    const addFree = (name) => {
        setRows((prev) => [...prev, { bonus_type_id: "", name, amount: 0, visibility: "intern", external_price: 0 }]);
    };
    return (
        <div data-testid={testid}>
            <Label className="text-xs font-bold uppercase tracking-wider flex items-center gap-1.5"><Gift className="w-3.5 h-3.5 text-emerald-600" /> Boni &amp; Aktionen (Abzug)<InfoHint id="dispo.items.boni" side="right" defaultText="Aus deinem Boni-Katalog / Herstelleraktionen — mindern den Kaufpreis (negativ). Extern = wird im Kaufvertrag gedruckt (nur eine externe Aktion möglich) und beim Verkauf als Artikel in die Warenwirtschaft eingebucht; intern = nur Boni-Nachverfolgung." /><SettingsShortcut to="/dashboard?tab=settings#settings-boni-katalog" title="Boni-Katalog / Hersteller- & Händleraktionen verwalten" testid="dispo-boni-settings-gear" /></Label>
            <div className="mb-1.5" />
            <CatalogSearchAdd catalog={catalog} onAdd={add} onFree={addFree} testid={testid} accent="emerald"
                placeholder="Bonus / Aktion suchen — oder eigene Aktion eintippen & hinzufügen…" emptyText="Bonus / Aktion suchen oder eigene eintippen…" />
            {rows.length === 0 && <div className="text-xs text-gray-400 py-1.5">Noch nichts ausgewählt.</div>}
            {rows.map((r, idx) => (
                <div key={r.bonus_type_id || idx} className="flex items-center gap-2 py-1.5 border-b border-gray-100" data-testid={`${testid}-row`}>
                    <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate flex items-center gap-1.5">
                            {r.name}
                            {r.advantage_only ? (
                                <span className="text-[9px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 rounded-sm px-1 py-0.5" data-testid={`${testid}-badge-advantage`}>Preisvorteil · nur Anzeige</span>
                            ) : (r.visibility || "intern") === "extern" ? (
                                <span className="text-[9px] font-bold uppercase tracking-wider bg-sky-100 text-sky-800 rounded-sm px-1 py-0.5" data-testid={`${testid}-badge-extern`}>Extern · Vertrag</span>
                            ) : (
                                <span className="text-[9px] font-bold uppercase tracking-wider bg-gray-100 text-gray-500 rounded-sm px-1 py-0.5">Intern</span>
                            )}
                        </div>
                    </div>
                    <span className={`text-xs font-bold ${r.advantage_only ? "text-amber-600" : "text-emerald-600"}`}>{r.advantage_only ? "" : "−"}</span>
                    <Input type="number" min="0" step="0.01" value={r.amount} onChange={(e) => upd(idx, "amount", e.target.value)} className="w-28 h-8 text-xs" placeholder="Betrag €" data-testid={`${testid}-amount`} />
                    {r.advantage_only ? (
                        <span className="text-sm font-bold w-24 text-right text-amber-700" title="Wird nur als Preisvorteil angezeigt — kein Abzug">{formatPrice(Number(r.amount) || 0)}</span>
                    ) : (
                        <span className="text-sm font-bold w-24 text-right text-emerald-700">−{formatPrice(Number(r.amount) || 0)}</span>
                    )}
                    <button type="button" onClick={() => rm(idx)} className="text-red-500 hover:text-red-700" data-testid={`${testid}-remove`}><XCircle className="w-4 h-4" /></button>
                </div>
            ))}
        </div>
    );
}

// ---------- Freitext-Posten (optionaler Betrag) -----------------------
function FreetextRows({ rows, setRows, testid }) {
    const [text, setText] = useState("");
    const [amount, setAmount] = useState("");
    const add = () => {
        const name = text.trim();
        if (!name) return;
        setRows((prev) => [...prev, { name, amount: Number(amount) || 0 }]);
        setText(""); setAmount("");
    };
    const upd = (idx, k, v) => setRows((prev) => prev.map((r, i) => i === idx ? { ...r, [k]: v } : r));
    const rm = (idx) => setRows((prev) => prev.filter((_, i) => i !== idx));
    return (
        <div data-testid={testid}>
            <Label className="text-xs font-bold uppercase tracking-wider flex items-center gap-1.5"><Type className="w-3.5 h-3.5 text-gray-600" /> Sonstige Absprachen/Vereinbarungen<InfoHint id="dispo.items.freetext" side="right" defaultText="Freie Position oder Bemerkung. Betrag optional (leer = nur Hinweis, erscheint auf dem Kaufvertrag)." /></Label>
            <div className="mb-1.5" />
            <div className="flex items-center gap-2">
                <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} placeholder="Bezeichnung / Bemerkung…" className="flex-1 h-9 text-sm" data-testid={`${testid}-text`} />
                <Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Betrag €" className="w-28 h-9 text-sm" data-testid={`${testid}-amount`} />
                <Button type="button" onClick={add} className="h-9 bg-gray-800 hover:bg-black text-white" data-testid={`${testid}-add`}><Plus className="w-4 h-4" /></Button>
            </div>
            {rows.length === 0 && <div className="text-xs text-gray-400 py-1.5">Noch nichts erfasst.</div>}
            {rows.map((r, idx) => (
                <div key={idx} className="flex items-center gap-2 py-1.5 border-b border-gray-100" data-testid={`${testid}-row`}>
                    <Input value={r.name} onChange={(e) => upd(idx, "name", e.target.value)} className="flex-1 h-8 text-sm" data-testid={`${testid}-row-text`} />
                    <Input type="number" step="0.01" value={r.amount} onChange={(e) => upd(idx, "amount", e.target.value)} className="w-28 h-8 text-xs" data-testid={`${testid}-row-amount`} />
                    <span className="text-sm font-bold w-24 text-right">{formatPrice(Number(r.amount) || 0)}</span>
                    <button type="button" onClick={() => rm(idx)} className="text-red-500 hover:text-red-700" data-testid={`${testid}-remove`}><XCircle className="w-4 h-4" /></button>
                </div>
            ))}
        </div>
    );
}

export function DispoItemsModal({ listingId, existing, accCatalog = [], svcCatalog = [], montageRate: montageRateProp = null, onBack, onClose, onSaved }) {
    const gearsOk = useGearsVisible();

    const [accCat, setAccCat] = useState(accCatalog?.length ? accCatalog : []);
    const [svcCat, setSvcCat] = useState(svcCatalog?.length ? svcCatalog : []);
    const [pkgCat, setPkgCat] = useState([]);
    const [boniCat, setBoniCat] = useState([]);
    const [labourCat, setLabourCat] = useState([]);
    const [accessories, setAccessories] = useState(existing?.accessories || []);
    const [services, setServices] = useState(existing?.services || []);
    const [boni, setBoni] = useState(existing?.boni || []);
    const [freetext, setFreetext] = useState(existing?.freetext || []);
    const [saving, setSaving] = useState(false);
    const [explodingPkg, setExplodingPkg] = useState(false);
    const [creatingWO, setCreatingWO] = useState(false);
    // Jun-2026 (User-Wunsch) — HU/TÜV-Status hierher verschoben (aus Auslieferung).
    const [tuev, setTuev] = useState(null);
    const tuevAutoAddedRef = useRef(false);
    const bereitstellungAddedRef = useRef(false);
    // Jul-2026 (User-Wunsch) — Versicherungskennzeichen: nach Auswahl fragen, ob
    // der Kunde es selbst besorgt oder es aus dem Lagerbestand entnommen wird.
    const [versKennzPrompt, setVersKennzPrompt] = useState(null);
    const [plateStock, setPlateStock] = useState([]);

    // Jul-2026 (User-Wunsch) — Vorausgewählte Dienstleistungen (Zahnrad-Einstellung):
    // immer automatisch aufnehmen; `locked` = Pflicht (nicht entfernbar).
    const [svcDefaults, setSvcDefaults] = useState([]);
    const [svcDefaultsOpen, setSvcDefaultsOpen] = useState(false);
    const svcDefaultsAppliedRef = useRef(false);
    useEffect(() => {
        // Hubraum-gefiltert (Jul-2026): Backend liefert nur die zur ccm-Angabe
        // des Fahrzeugs passenden Vorauswahl-Positionen.
        api.get("/dealer/dispo/default-service-items", { params: listingId ? { listing_id: listingId } : {} })
            .then((r) => setSvcDefaults(r.data?.items || [])).catch(() => {});
    }, [listingId]);
    const defaultRow = (d) => ({ article_id: d.article_id, sku: d.sku, name: d.name, category: d.category || "sales", qty: Number(d.qty) || 1, unit_price_gross: Number(d.unit_price_gross || 0), locked: Boolean(d.locked), preselected: true });
    useEffect(() => {
        if (svcDefaultsAppliedRef.current || !svcDefaults.length) return;
        svcDefaultsAppliedRef.current = true;
        // Jul-2026 (User-Report „nur Bereitstellung wird angezeigt"): Vorauswahl-
        // Positionen IMMER ergänzen — außer der Nutzer hat sie in diesem Prozess
        // ausdrücklich entfernt (removed_preselected). Pflicht-Positionen immer.
        const removed = new Set(existing?.removed_preselected || []);
        setServices((prev) => {
            let next = [...prev];
            svcDefaults.forEach((d) => {
                const i = next.findIndex((r) => r.article_id === d.article_id && !r.montage_for);
                if (i >= 0) { if (d.locked && !next[i].locked) next[i] = { ...next[i], locked: true }; return; }
                if (d.locked || !removed.has(d.article_id)) next.push(defaultRow(d));
            });
            // Jul-2026 (User-Report): veraltete Pflicht-Markierungen auflösen —
            // „Pflicht" gilt nur noch, wenn die Position AKTUELL in der Vorauswahl
            // als Pflicht steht (oder Bereitstellung ist).
            const lockedIds = new Set(svcDefaults.filter((d) => d.locked).map((d) => d.article_id));
            next = next.map((r) => (r.locked && !r.montage_for && !isBereitstellung(r) && !lockedIds.has(r.article_id))
                ? { ...r, locked: false } : r);
            return next;
        });
    }, [svcDefaults]); // eslint-disable-line react-hooks/exhaustive-deps
    const handleDefaultsSaved = async (items) => {
        // Für das aktuelle Fahrzeug ccm-gefiltert nachladen (Hubraum-Bindung).
        let filtered = items;
        try {
            const r = await api.get("/dealer/dispo/default-service-items", { params: listingId ? { listing_id: listingId } : {} });
            filtered = r.data?.items || [];
        } catch { /* fallback: ungefiltert */ }
        setSvcDefaults(filtered);
        const lockedIds = new Set(filtered.filter((d) => d.locked).map((d) => d.article_id));
        setServices((prev) => {
            let next = prev.map((r) => (!r.montage_for && r.preselected && !lockedIds.has(r.article_id) && r.locked && !isBereitstellung(r))
                ? { ...r, locked: false } : r);
            filtered.forEach((d) => {
                const i = next.findIndex((r) => r.article_id === d.article_id && !r.montage_for);
                if (i >= 0) { if (d.locked) next[i] = { ...next[i], locked: true }; }
                else next.push(defaultRow(d));
            });
            return next;
        });
    };

    // Jul-2026 (User-Wunsch) — Hersteller-Zubehör-Link (markengesteuert):
    // Zahnrad im Zubehör-Bereich + passender Link neben der Überschrift.
    const [accLinksOpen, setAccLinksOpen] = useState(false);
    const [accBrandLink, setAccBrandLink] = useState(null);
    const [accMontageFlat, setAccMontageFlat] = useState(0);
    const loadAccBrandLink = useCallback(() => {
        api.get("/dealer/dispo/accessory-brand-links", { params: listingId ? { listing_id: listingId } : {} })
            .then((r) => {
                setAccBrandLink(r.data?.matched || null);
                setAccMontageFlat(Number(r.data?.montage_flat_gross ?? 40));
            }).catch(() => {});
    }, [listingId]);
    useEffect(() => { loadAccBrandLink(); }, [loadAccBrandLink]);

    // Jul-2026 (User-Wunsch) — am häufigsten benutzte Artikel zuerst vorschlagen.
    const [topArticles, setTopArticles] = useState([]);
    useEffect(() => {
        api.get("/dealer/dispo/top-articles")
            .then((r) => setTopArticles((r.data?.items || []).map((a) => ({ ...a, _top: true }))))
            .catch(() => {});
    }, []);

    const handleServiceAdd = (art) => {
        if (isVersKennzeichen(art)) {
            setVersKennzPrompt(art);
            api.get("/articles", { params: { category: "insurance_plate" } })
                .then((r) => setPlateStock(Array.isArray(r.data) ? r.data : (r.data?.items || [])))
                .catch(() => setPlateStock([]));
        }
        if (isTuevErneuern(art)) {
            // Restlaufzeit ≤ 6 Monate (needs_action) → TÜV kostenlos (0 €), sonst kostenpflichtig.
            const free = tuev ? (tuev.needs_action || (tuev.months_left != null && Number(tuev.months_left) <= 6)) : false;
            if (free) {
                setServices((prev) => prev.map((r) => (r.article_id === art.id ? { ...r, unit_price_gross: 0, tuev_free: true } : r)));
                toast.message("HU/TÜV: Restlaufzeit ≤ 6 Monate — TÜV wird kostenlos erneuert (0 €).");
            } else {
                toast.message("HU/TÜV: Restlaufzeit > 6 Monate — TÜV ist kostenpflichtig.");
            }
        }
    };
    const markPlateSource = (source, plateArt = null) => {
        setServices((prev) => prev.map((r) => isVersKennzeichen(r) ? { ...r, plate_source: source } : r));
        if (plateArt) {
            setAccessories((prev) => prev.some((r) => r.article_id === plateArt.id && !r.montage_for)
                ? prev
                : [...prev, { article_id: plateArt.id, sku: plateArt.sku, name: plateArt.name, category: plateArt.category || "insurance_plate", qty: 1, unit_price_gross: Number(plateArt.sell_price_gross || plateArt.list_price_gross || 0) }]);
            toast.success(`Versicherungskennzeichen „${plateArt.name}" aus dem Lagerbestand übernommen.`);
        } else {
            toast.message("Versicherungskennzeichen: Kunde besorgt es selbst.");
        }
        setVersKennzPrompt(null);
    };

    useEffect(() => {
        if (!listingId) return;
        api.get(`/listings/${listingId}/dispo/tuev-check`)
            .then((r) => setTuev(r.data)).catch(() => setTuev(null));
    }, [listingId]);

    useEffect(() => { if (accCatalog?.length) setAccCat(accCatalog); }, [accCatalog]);
    useEffect(() => { if (svcCatalog?.length) setSvcCat(svcCatalog); }, [svcCatalog]);
    useEffect(() => {
        let alive = true;
        const tag = (arr, cat) => Array.isArray(arr) ? arr.map((a) => ({ ...a, category: a.category || cat })) : [];
        Promise.all([
            api.get("/articles", { params: { category: "accessory" } }).then((r) => r.data).catch(() => null),
            api.get("/articles", { params: { category: "labour" } }).then((r) => r.data).catch(() => null),
            api.get("/articles", { params: { category: "tire" } }).then((r) => r.data).catch(() => null),
            api.get("/articles", { params: { category: "part" } }).then((r) => r.data).catch(() => null),
            api.get("/articles", { params: { category: "sales" } }).then((r) => r.data).catch(() => null),
            api.get("/articles", { params: { category: "package" } }).then((r) => r.data).catch(() => null),
            api.get("/dealer/bonus-types", { params: listingId ? { listing_id: listingId } : {} }).then((r) => r.data?.items).catch(() => null),
        ]).then(([acc, lab, tir, par, s, p, b]) => {
            if (!alive) return;
            // Jun-2026 (User-Wunsch) — Zubehör + Lohn(AW) + Reifen + Ersatzteile in
            // einer Suche (keine Kategorie-Umschaltung nötig).
            const merged = [...tag(acc, "accessory"), ...tag(lab, "labour"), ...tag(tir, "tire"), ...tag(par, "part")];
            if (merged.length) setAccCat(merged);
            // Lohn/AW-Artikel separat halten — wird vom accCatalog-Prop-Effekt NICHT
            // überschrieben (Quelle für den Montage-Lohnsatz).
            if (Array.isArray(lab)) setLabourCat(lab);
            if (Array.isArray(s)) setSvcCat(tag(s, "sales"));
            if (Array.isArray(p)) setPkgCat(p);
            if (Array.isArray(b)) setBoniCat(b);
        });
        return () => { alive = false; };
    }, [listingId]);

    // Jun-2026 (User-Wunsch) — Standard-Montage-Lohnsatz (AW-Satz) aus den geladenen
    // Lohn/AW-Artikeln ableiten (Artikel mit is_montage_rate). sell_price_gross = €/AW.
    // Jun-2026 (User-Wunsch) — Standard-Montage-Lohnsatz (AW-Satz): bevorzugt vom
    // Parent geladen (zuverlässig), Fallback auf den aus dem Katalog abgeleiteten
    // Lohn/AW-Artikel (is_montage_rate). sell_price_gross = €/AW.
    const montageRate = useMemo(
        () => montageRateProp || (labourCat || []).find((a) => a.is_montage_rate) || null,
        [montageRateProp, labourCat],
    );

    // Map accessoryArticleId → Montage-AW (assembly_aw) aus dem Katalog.
    const awMap = useMemo(() => {
        const m = {};
        (accCat || []).forEach((a) => { const aw = Number(a.assembly_aw || 0); if (aw > 0) m[a.id] = aw; });
        return m;
    }, [accCat]);

    // Jul-2026 (User-Wunsch) — serverseitige Artikel-Suche über ALLE WMS-Kategorien
    // (vollständig + schnell, ersetzt das Vorab-Filtern nach einzelnen Kategorien).
    const searchArticles = useCallback(async (term) => {
        const r = await api.get("/articles", { params: { search: term, limit: 40 } });
        return Array.isArray(r.data) ? r.data : (r.data?.items || []);
    }, []);

    // Jun-2026 (User-Wunsch) — HU/TÜV automatisch als Dienstleistung in den
    // Auftrag aufnehmen, wenn die Restlaufzeit < 6 Monate beträgt (einmalig).
    useEffect(() => {
        if (!tuev || !tuev.needs_action || tuev.tuev_neu || tuevAutoAddedRef.current) return;
        tuevAutoAddedRef.current = true;
        setServices((prev) => {
            if (prev.some((r) => r.auto_tuev || /hu\/?tüv|hauptunters/i.test(r.name || ""))) return prev;
            const match = (svcCat || []).find((a) => /tüv|hauptunters|\bhu\b/i.test(a.name || ""));
            // Restlaufzeit < 6 Monate → TÜV kostenlos (0 €).
            const row = match
                ? { article_id: match.id, sku: match.sku, name: match.name, category: "sales", qty: 1, unit_price_gross: 0, auto_tuev: true, tuev_free: true }
                : { article_id: "auto-tuev", name: "HU/TÜV (Hauptuntersuchung)", category: "sales", qty: 1, unit_price_gross: 0, auto_tuev: true, tuev_free: true };
            return [...prev, row];
        });
        toast.message("HU/TÜV automatisch als Dienstleistung aufgenommen (Restlaufzeit < 6 Monate) — kostenlos.");
    }, [tuev, svcCat]);

    // Jul-2026 (User-Wunsch) — Bereitstellungskosten IMMER automatisch aufnehmen
    // (Pflicht-Position, nicht entfernbar), sobald der Katalog geladen ist.
    useEffect(() => {
        if (bereitstellungAddedRef.current) return;
        const match = (svcCat || []).find((a) => isBereitstellung(a));
        if (!match) return;
        bereitstellungAddedRef.current = true;
        setServices((prev) => {
            if (prev.some((r) => isBereitstellung(r))) {
                return prev.map((r) => (isBereitstellung(r) ? { ...r, locked: true } : r));
            }
            return [...prev, { article_id: match.id, sku: match.sku, name: match.name, category: "sales", qty: 1, unit_price_gross: Number(match.sell_price_gross || 0), locked: true }];
        });
    }, [svcCat]);

    // Summe: Zubehör + Dienstleistungen + Freitext (positiv) − Boni/Aktionen.
    const total = useMemo(() => {
        const items = [...accessories, ...services].reduce((s, r) => s + (Number(r.qty) || 0) * (Number(r.unit_price_gross) || 0), 0);
        const ft = freetext.reduce((s, r) => s + (Number(r.amount) || 0), 0);
        // Jun-2026 (User-Wunsch) — nur externe Boni/Aktionen mindern den Kundenpreis.
        const bn = boni.filter((r) => (r.visibility || "intern") === "extern" && !r.advantage_only).reduce((s, r) => s + (Number(r.amount) || 0), 0);
        return items + ft - bn;
    }, [accessories, services, freetext, boni]);

    const triggerArticles = useMemo(() => {
        const byId = {};
        [...accCat, ...svcCat].forEach((a) => { byId[a.id] = a; });
        const hits = [];
        [...accessories, ...services].forEach((r) => {
            const a = byId[r.article_id];
            if (a?.auto_create_workshop_order) hits.push(a.name);
        });
        return hits;
    }, [accessories, services, accCat, svcCat]);

    const addPackage = async (pkgId) => {
        const pkg = pkgCat.find((p) => p.id === pkgId);
        if (!pkg) return;
        setExplodingPkg(true);
        try {
            const { data } = await api.get(`/articles/${pkgId}/package-explode`);
            let mode = data.mode;
            if (mode === "ask") {
                mode = window.confirm(
                    `Paket „${data.package_name}" übernehmen:\n\nOK = aufgelöst (Einzelposten)\nAbbrechen = als Sammelposten`
                ) ? "exploded" : "lump";
            }
            if (mode === "lump") {
                setAccessories((prev) => prev.some((r) => r.article_id === data.lump_row.article_id && r.name === data.lump_row.name)
                    ? prev : [...prev, data.lump_row]);
            } else {
                setAccessories((prev) => {
                    const next = [...prev];
                    (data.component_rows || []).forEach((row) => {
                        if (!next.some((r) => r.article_id === row.article_id)) next.push(row);
                    });
                    return next;
                });
                if ((data.service_rows || []).length) {
                    setServices((prev) => {
                        const next = [...prev];
                        data.service_rows.forEach((row) => {
                            if (!next.some((r) => r.sku === row.sku)) next.push(row);
                        });
                        return next;
                    });
                }
            }
            if ((data.missing_component_ids || []).length) {
                toast.warning(`${data.missing_component_ids.length} Bestandteil(e) nicht gefunden`);
            }
            toast.success(`Paket „${data.package_name}" übernommen`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Paket-Auflösung fehlgeschlagen");
        } finally { setExplodingPkg(false); }
    };

    const createWorkshopOrder = async () => {
        setCreatingWO(true);
        try {
            const { data } = await api.post(`/listings/${listingId}/dispo/workshop-order`);
            const num = data?.dispo_workshop_order?.order_number;
            toast.success(data?.already_exists
                ? `Werkstattauftrag besteht bereits (${num || ""})`
                : `Werkstattauftrag angelegt${num ? ` (${num})` : ""}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Werkstattauftrag konnte nicht angelegt werden");
        } finally { setCreatingWO(false); }
    };

    // Jul-2026 (User-Wunsch) — kostenpflichtige Positionen einer Aktion (WMS/Frei)
    // automatisch unter Zubehör bzw. Lohn einsetzen; Kunde zahlt diese Positionen.
    const applyBonusPaidItems = (b) => {
        const items = Array.isArray(b.paid_items) ? b.paid_items : [];
        if (!items.length) return;
        const mk = (i) => ({
            article_id: i.article_id || "", sku: i.sku || "", name: i.name,
            category: i.category || (i.kind === "labour" ? "labour" : "accessory"),
            qty: Number(i.qty) || 1, unit_price_gross: Number(i.unit_price_gross) || 0,
            bonus_source: b.id,
        });
        const acc = items.filter((i) => (i.kind || "article") !== "labour").map(mk);
        const lab = items.filter((i) => i.kind === "labour").map(mk);
        if (acc.length) setAccessories((prev) => [...prev, ...acc.filter((i) => !prev.some((r) => r.name === i.name && r.bonus_source === b.id))]);
        if (lab.length) setServices((prev) => [...prev, ...lab.filter((i) => !prev.some((r) => r.name === i.name && r.bonus_source === b.id))]);
        toast.message(`${items.length} Position(en) aus der Aktion „${b.name}" automatisch übernommen — vom Kunden zu zahlen.`);
    };

    const save = async ({ silent = false, advance = false } = {}) => {
        // Jul-2026 (User-Wunsch) — genau EINE Transport-Position ist Pflicht,
        // bevor der Schritt abgeschlossen wird (Händlernetz / Bundesgebiet / Kunde-selbst).
        if (advance && !silent && !hasTransportService(services)) {
            toast.error("Bitte eine Transport-Option wählen (Händlernetz, Bundesgebiet oder Kunde holt selbst ab).");
            return false;
        }
        if (!silent) setSaving(true);
        try {
            const norm = (rows) => rows.map((r) => ({ ...r, qty: Number(r.qty) || 0, unit_price_gross: Number(r.unit_price_gross) || 0 }));
            const normBoni = boni.map((r) => ({ bonus_type_id: r.bonus_type_id, name: r.name, amount: Number(r.amount) || 0, visibility: r.visibility || "intern", external_price: Number(r.external_price) || 0, advantage_only: !!r.advantage_only }));
            const normFt = freetext.map((r) => ({ name: r.name, amount: Number(r.amount) || 0 }));
            // Jul-2026 (User-Report): entfernte Vorauswahl-Positionen merken, damit
            // sie beim erneuten Öffnen nicht wieder automatisch erscheinen.
            const removedPre = svcDefaults
                .filter((d) => !d.locked && !services.some((r) => r.article_id === d.article_id && !r.montage_for))
                .map((d) => d.article_id);
            const r = await api.patch(`/listings/${listingId}/dispo/items`, {
                accessories: norm(accessories), services: norm(services), boni: normBoni, freetext: normFt,
                removed_preselected: removedPre,
            });
            if (!silent) {
                toast.success("Zubehör, Dienstleistungen & Aktionen gespeichert");
                autosave.markSaved();
                if (advance) await onSaved?.(r.data.dispo_items);
            }
            return true;
        } catch (e) {
            if (!silent) toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
            return false;
        } finally { if (!silent) setSaving(false); }
    };
    const autosave = useAutosaveOnBlur(
        () => save({ silent: true }),
        () => JSON.stringify({ accessories, services, boni, freetext }),
    );

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent onPointerDownOutside={(e) => e.preventDefault()} onInteractOutside={(e) => e.preventDefault()} className="w-[96vw] max-w-[1500px] max-h-[92vh] overflow-y-auto" data-testid="dispo-modal-items" onBlur={autosave.onBlur}>
                <FeatureGuideTriangle guideKey="items" />
                <DialogHeader><DialogTitle className="flex items-center gap-2"><Package className="w-5 h-5 text-orange-600" /> Zubehör &amp; Dienstleistungen<span className="ml-auto text-[10px] font-bold uppercase tracking-wider text-gray-400 mr-6">Schritt 2 von 6</span></DialogTitle></DialogHeader>
                <AutosaveBadge status={autosave.status} savedAt={autosave.savedAt} />

                {/* Jul-2026 (User-Wunsch): „Pakete / Sets" aus diesem Schritt entfernt —
                    Paket-Übernahme läuft über die Artikel-Suche bzw. das WWS. */}

                {/* Jul-2026 (User-Wunsch): 2-Spalten-Layout auf breiten Bildschirmen —
                    weniger/kein Scrollen nötig. */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 items-start">
                <div className="min-w-0">
                <DispoItemsRows title="Zubehör, Lohn (AW), Reifen & Ersatzteile (WMS)" hintId="dispo.items.accessories" hint="Eine Suche über alle WMS-Artikel: Zubehör, Lohn/Arbeitswerte (AW), Reifen und Ersatzteile — keine Kategorie-Umschaltung nötig. Zubehör mit hinterlegten Montage-AW ergänzt automatisch eine Montage-Lohnposition (AW × AW-Satz); ohne AW wird die Montage-Pauschale je Artikel aufgenommen (Zahnrad)." catalog={accCat} rows={accessories} setRows={setAccessories} testid="dispo-items-accessories" montageRate={montageRate} awMap={awMap} searchFn={searchArticles} montageFlat={accMontageFlat} topSuggestions={topArticles}
                    headerExtra={
                        <>
                            {gearsOk && <button type="button" onClick={() => setAccLinksOpen(true)}
                                className="text-gray-400 hover:text-gray-700 transition-colors"
                                title="Hersteller-Zubehörseiten konfigurieren (Link je Marke)"
                                data-testid="dispo-accessory-links-gear">
                                <Settings className="w-3.5 h-3.5" />
                            </button>}
                            {accBrandLink && (
                                <a href={accBrandLink.url} target="_blank" rel="noopener noreferrer"
                                    className="ml-auto inline-flex items-center gap-1 text-[11px] font-bold normal-case tracking-normal text-white bg-orange-500 border border-orange-600 rounded-sm px-2.5 py-1 hover:bg-orange-600 transition-colors animate-pulse hover:animate-none shadow-sm"
                                    title={`Zubehör beim Hersteller ansehen: ${accBrandLink.url}`}
                                    data-testid="dispo-accessory-brand-link">
                                    <Package className="w-3.5 h-3.5" /> Zubehör: {accBrandLink.short_label}
                                </a>
                            )}
                        </>
                    } />
                <div className="border-t my-3 lg:hidden" />
                </div>
                <div className="min-w-0">
                {tuev && (tuev.tuev_until || tuev.tuev_neu) && (
                    tuev.tuev_neu ? (
                        <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-2.5 text-xs text-emerald-800 flex items-center gap-2 mb-2" data-testid="dispo-items-tuev-neu">
                            <ShieldCheck className="w-4 h-4" /> Neue HU/TÜV bei Verkauf — keine HU/TÜV-Dienstleistung nötig.
                        </div>
                    ) : tuev.needs_action ? (
                        <div className="bg-amber-50 border border-amber-300 rounded-sm p-2.5 text-xs text-amber-900 flex items-center gap-2 mb-2" data-testid="dispo-items-tuev-warning">
                            <AlertTriangle className="w-4 h-4" /> HU/TÜV {tuev.tuev_until ? `bis ${tuev.tuev_until}` : "—"}{tuev.months_left != null ? ` (noch ${tuev.months_left} Monate)` : ""} — Restlaufzeit &lt; 6 Monate. HU/TÜV wurde automatisch als Dienstleistung aufgenommen.
                        </div>
                    ) : (
                        <div className="bg-gray-50 border border-gray-200 rounded-sm p-2.5 text-xs text-gray-600 flex items-center gap-2 mb-2" data-testid="dispo-items-tuev-ok">
                            <ShieldCheck className="w-4 h-4 text-emerald-600" /> HU/TÜV {tuev.tuev_until ? `bis ${tuev.tuev_until}` : "—"}{tuev.months_left != null ? ` (noch ${tuev.months_left} Monate)` : ""} — ausreichend.
                        </div>
                    )
                )}
                <DispoItemsRows title="Dienstleistungen" hintId="dispo.items.services" hint="Suche über alle WMS-Artikel (Verkauf/Vertrieb, Dienstleistungen u. a.). Hinweis: Bei Zulassung und Transport ist jeweils nur EINE Option wählbar." catalog={svcCat} rows={services} setRows={setServices} testid="dispo-items-services" searchFn={searchArticles} groupOf={serviceExclusiveGroup} onAfterAdd={handleServiceAdd}
                    headerExtra={gearsOk ? (
                        <button type="button" onClick={() => setSvcDefaultsOpen(true)}
                            className="text-gray-400 hover:text-gray-700 transition-colors"
                            title="Vorausgewählte Dienstleistungen konfigurieren (immer im Kaufprozess, optional Pflicht, ccm-Bindung)"
                            data-testid="dispo-service-defaults-gear">
                            <Settings className="w-3.5 h-3.5" />
                        </button>
                    ) : null} />
                </div>
                {/* Jul-2026 (User-Wunsch): deutlicher Abstand + Trennlinie, damit Boni/
                    Freitext nicht wie Teil der Dienstleistungen wirken. */}
                <div className="min-w-0 mt-6 pt-4 border-t-2 border-gray-200">
                <BoniRows catalog={boniCat} rows={boni} setRows={setBoni} testid="dispo-items-boni" onAdded={applyBonusPaidItems} />
                </div>
                <div className="min-w-0 mt-6 pt-4 border-t-2 border-gray-200">
                <FreetextRows rows={freetext} setRows={setFreetext} testid="dispo-items-freetext" />
                </div>
                </div>

                {triggerArticles.length > 0 && (
                    <div className="flex items-start gap-2 bg-sky-50 border border-sky-200 rounded-sm px-3 py-2 mt-2 text-xs text-sky-900" data-testid="dispo-items-workshop-hint">
                        <Wrench className="w-4 h-4 mt-0.5 shrink-0" />
                        <div className="flex-1">
                            <span><span className="font-bold">Werkstattauftrag empfohlen</span> — die Artikel {triggerArticles.join(", ")} lösen einen Werkstattauftrag aus.</span>
                            <div className="mt-1.5">
                                <Button type="button" size="sm" onClick={createWorkshopOrder} disabled={creatingWO}
                                    className="h-7 bg-sky-600 hover:bg-sky-700 text-white text-[11px] uppercase tracking-wider font-bold" data-testid="dispo-items-create-workshop">
                                    {creatingWO ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Wrench className="w-3.5 h-3.5 mr-1" /> Werkstattauftrag anlegen</>}
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                <div className="flex items-center justify-between bg-orange-50 border border-orange-200 rounded-sm px-3 py-2 mt-3" data-testid="dispo-items-total">
                    <span className="text-sm font-bold">Summe Zubehör, DL &amp; Aktionen (brutto)</span>
                    <span className="text-lg font-black text-orange-700" data-testid="dispo-items-total-value">{formatPrice(total)}</span>
                </div>
                <DialogFooter>
                    <DispoBackButton onBack={onBack ? () => { save({ silent: true }); onBack(); } : undefined} />
                    <Button variant="outline" onClick={onClose}>Abbrechen</Button>
                    <Button onClick={() => save({ silent: false, advance: true })} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="dispo-items-submit">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Weiter"}
                    </Button>
                </DialogFooter>
            </DialogContent>

            {versKennzPrompt && (
                <Dialog open onOpenChange={(o) => { if (!o) setVersKennzPrompt(null); }}>
                    <DialogContent className="max-w-md" data-testid="verskennz-source-dialog">
                        <DialogHeader><DialogTitle>Versicherungskennzeichen</DialogTitle></DialogHeader>
                        <p className="text-sm text-gray-600">
                            Besorgt der Kunde das Versicherungskennzeichen selbst, oder soll eines aus dem
                            Lagerbestand des Händlers entnommen werden?
                        </p>
                        <div className="mt-3">
                            <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Aus Lagerbestand wählen</Label>
                            <select
                                className="w-full text-sm border-2 border-gray-300 rounded-sm px-3 py-2 bg-white mt-1"
                                value=""
                                onChange={(e) => {
                                    const p = plateStock.find((x) => x.id === e.target.value);
                                    if (p) markPlateSource("stock", p);
                                }}
                                data-testid="verskennz-stock-select"
                            >
                                <option value="">{plateStock.length ? "+ Kennzeichen aus Lager wählen…" : "Kein Lagerbestand gefunden"}</option>
                                {plateStock.map((p) => (
                                    <option key={p.id} value={p.id}>{p.name}{p.sku ? ` · ${p.sku}` : ""} ({formatPrice(p.sell_price_gross || 0)})</option>
                                ))}
                            </select>
                        </div>
                        <DialogFooter className="mt-2">
                            <Button variant="outline" onClick={() => markPlateSource("customer")} data-testid="verskennz-customer-btn">
                                Kunde besorgt selbst
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}
            {svcDefaultsOpen && (
                <ServiceDefaultsDialog open onClose={() => setSvcDefaultsOpen(false)}
                    searchFn={searchArticles} onSaved={handleDefaultsSaved} />
            )}
            {accLinksOpen && (
                <AccessoryLinksDialog open onClose={() => setAccLinksOpen(false)} onSaved={loadAccBrandLink} />
            )}
        </Dialog>
    );
}
